import { filterRulesMapped } from '@/tools/filterRules'
import {
    dateSortFunction,
    getDateTimeRange,
    getChildrenFromList,
    arrayToTextFormatted,
    selectorDateTimeValidation,
    reportTimeRangeDetail } from '@/tools/functions'
import SelectorComponent from '@/components/commons/selector/SelectorComponent.vue'
import { commandService } from '@/services/command.service'
import { thingService } from '@/services/thing.service'
import { configurationService } from '@/services/configuration.service'
import i18n from '@/i18n'
import { SelectorModel } from '@/constants/constants'
import { clone } from 'ramda'
import Vue from 'vue'
import { mapActions, mapGetters } from 'vuex'
import BreadcrumbComponent from '@/components/commons/report-breadcrumb/BreadcrumbComponent.vue'

const COMPONENT_ID = 'SENT_COMMANDS_REPORT'

export default {
    name: 'SentCommandsReport',
    components: {
        SelectorComponent,
        BreadcrumbComponent
    },
    data: () => ({
        // datos para la tabla
        tableHeaders: [],
        tableFilterRules: {},
        tableData: [],
        tableDataKey: '_id',
        tableComponentButtons: [],
        loadingTable: false,
        rowActions: [],
        disableRefreshTable: true,
        tableSettingBtnDisabled: false,
        tableConfig: {},
        // para controlar el navigation drawer
        showSelector: false,
        // modelo del selector
        selectorModel: clone(SelectorModel),
        // backup de datos del selector
        selectedDateAndTimeRange: null,
        selectedDateAndTimeRangeCustomType: null,
        sinceDate: null,
        sinceTime: null,
        toDate: null,
        toTime: null,
        timestampFrom: null,
        timestampTo: null,
        things: [],
        autoReport: false,
        autoReportMinutes: null,
        // interval autoreporte
        intervalId: null,
        // breadcrumb
        selectedTab: 'tab-table',
        tabs: [
            {
                id: 'tab-table',
                name: i18n.t('sentCommandsReport.name')
            }
        ],
    }),
    computed: {
        ...mapGetters('reportBreadcrumb', [
            'getDetails'
        ])
    },
    created () {
        this.selectorModel.filters.push({
            id: 'things',
            name: i18n.t('equipment.name'),
            show: false,
            disabled: false,
            showDialog: false,
            singleSelect: false,
            data: [],
            selectedData: [],
            selectAction: undefined
        })
    },
    async mounted () {
        // cosas
        const things = await thingService.getAllForSelector()
        this.selectorModel.filters[0].data.cleanAndUpdate(things)

        // autoreporte
        this.selectorModel.showAutoReport = true

        // NO CARGO EL REPORTE, ABRO EL SELECTOR CON LA CONFIGURACION GUARDADA
        await this.getConfiguration()
        this.showSelector = true
    },
    beforeDestroy () {
        // interval autoreport
        clearTimeout(this.intervalId)
        this.intervalId = null
        // reset breadcrumb
        this.reset()
    },
    methods: {
        /**
         * actions del breadcrumb
         */
        ...mapActions('reportBreadcrumb', [
            'setDetails',
            'reset'
        ]),
        /**
         * Acción de actualizar datos de la tabla
         */
        refreshTable () {
            this.loadingTable = true
            this.disableRefreshTable = true

            // actualizo los timestamp del rango de tiempo
            const conversion = getDateTimeRange(this.selectedDateAndTimeRange, this.selectedDateAndTimeRangeCustomType,
                this.sinceDate, this.sinceTime, this.toDate, this.toTime)
            this.timestampFrom = conversion.tFrom
            this.timestampTo = conversion.tTo

            // obtengo los datos
            commandService.getSentCommandsReport(this.things.map(t => t.id), this.timestampFrom, this.timestampTo)
                .then(response => {
                    this.tableData.cleanAndUpdate(response.data)
                    this.loadingTable = false
                    this.disableRefreshTable = false
                })
        },
        /**
         * Obtener los datos del reporte luego de procesar los datos de los filtros
         * @param {*} eventData 
         */
        getReport (eventData) {
            this.showSelector = false
            this.loadingTable = true
            this.disableRefreshTable = true

            // conversión del rango de tiempo
            const conversion = getDateTimeRange(eventData.dateAndTimeRange, eventData.dateAndTimeRangeCustomType,
                eventData.sinceDate, eventData.sinceTime, eventData.toDate, eventData.toTime)
            this.selectedDateAndTimeRange = eventData.dateAndTimeRange
            this.selectedDateAndTimeRangeCustomType = eventData.dateAndTimeRangeCustomType
            this.sinceDate = eventData.sinceDate
            this.sinceTime = eventData.sinceTime
            this.toDate = eventData.toDate
            this.toTime = eventData.toTime
            this.timestampFrom = conversion.tFrom
            this.timestampTo = conversion.tTo

            // cosas seleccionadas
            this.things = []
            getChildrenFromList(eventData.filters[0].selectedData, this.things)

            // otros valores del selector
            this.autoReport = eventData.autoReport
            this.autoReportMinutes = eventData.autoReportMinutes

            // guardo la configuración
            configurationService.save(COMPONENT_ID, {
                tableConfig: this.tableConfig
            })

            // obtengo los datos
            commandService.getSentCommandsReport(this.things.map(t => t.id), this.timestampFrom, this.timestampTo)
                .then(response => {
                    this.tableData.cleanAndUpdate(response.data)
                    this.tableHeaders.cleanAndUpdate(response.headers)
                    this.setSortFunctions()
                    this.setTableFilterRules()
                    this.loadingTable = false
                    this.disableRefreshTable = false

                    // detalles del reporte
                    this.setReportDetails()

                    // autoreporte
                    if (this.autoReport) {
                        const that = this
                        // interval del auto-reporte
                        clearInterval(that.intervalId)
                        that.intervalId = setInterval(
                            function () {
                                that.refreshTable()
                            },
                            that.autoReportMinutes * 60 * 1000)
                    }
                })
        },
        /**
         * Para mostrar/ocultar el selector
         */
        selector () {
            this.showSelector = !this.showSelector
        },
        /**
         * Para setear las reglas de filtrado
         */
        setTableFilterRules () {
            this.tableFilterRules = {}
            this.tableHeaders.forEach(header => {
                this.tableFilterRules[header.value] = filterRulesMapped[header.filterType]
            })
        },
        /**
         * Para setear las funciones de ordenamiento
         */
        setSortFunctions () {
            /*
                Para ordenar las columnas fecha y hora
                TO DO: analizar la posibilidad de incluir este tipo de sorting en el componente genérico
            */
            this.tableHeaders.find(header => header.value === 'createDate').sort = (a, b) => { return dateSortFunction(a, b, 'DD/MM/YYYY HH:mm:ss') }
            this.tableHeaders.find(header => header.value === 'lastSendDate').sort = (a, b) => { return dateSortFunction(a, b, 'DD/MM/YYYY HH:mm:ss') }
        },
        /**
         * Manejador de acciones de los botones de las filas
         * @param {*} button 
         * @param {*} data 
         */
        // eslint-disable-next-line no-unused-vars
        rowButtonClicked (button, data) {
            switch (button.id) {
                default:
                    console.warn('Button action not implemented')
                    break
            }
        },
        /**
         * Para validar el selector de filtros y habilitar/deshabilitar el botón para guardar
         */
        validateSelector () {
            const that = this
            Vue.nextTick(function () {
                const thingsSelected = that.selectorModel.filters[0].selectedData
                const customTimeValidation = selectorDateTimeValidation(that.selectorModel.selectedDateAndTimeRange,
                    that.selectorModel.selectedDateAndTimeRangeCustomType, that.selectorModel.customDateTimeValidForm,
                    that.selectorModel.sinceDate, that.selectorModel.sinceTime, that.selectorModel.toDate, that.selectorModel.toTime)
                that.selectorModel.disableSaveButton =
                    thingsSelected.length === 0 || customTimeValidation
            })
        },
        /**
         * Para obtener la configuración de filtros
         * Esta función se ejecuta en mounted
         */
        async getConfiguration () {
            let initialLoad = true
            const config = await configurationService.get(COMPONENT_ID)

            if (config && config.data) {
                // config de la tabla
                if (config.data.tableConfig) {
                    this.tableConfig = config.data.tableConfig
                } else {
                    this.tableConfig = {}
                }
            } else {
                initialLoad = false
            }

            return initialLoad
        },
        /**
         * Acción guardar configuración del botón de la tabla
         * @param {*} tableConfig
         */
        saveConfig (tableConfig) {
            // guardo la configuración
            this.tableConfig = tableConfig;
            configurationService.save(COMPONENT_ID, {
                tableConfig
            }, true)
        },
        /**
         * Para cambiar de tab
         * @param {*} id
         */
        tabSelected (id) {
            this.selectedTab = id
        },
        /**
         * Detalles del reporte
         */
        setReportDetails () {
            // rango de tiempo formateado
            const timeRangeStr = reportTimeRangeDetail(this.selectedDateAndTimeRange, this.timestampFrom, this.timestampTo)

            // nombres de equipos formateados
            const thingNames = this.things.map(thing => thing.name)
            const thingsTextFormatted = arrayToTextFormatted(thingNames)

            let details = `<h3>${this.$t('sentCommandsReport.name')}</h3>`
            details += `<h5>${this.$t('equipment.name')}: ${thingsTextFormatted}</h5>`
            details += `<h5>${timeRangeStr.timeRange}</h5>`
            if (timeRangeStr.from) {
                details += `<h5>${timeRangeStr.from}</h5>`
            }
            if (timeRangeStr.to) {
                details += `<h5>${timeRangeStr.to}</h5>`
            }

            this.setDetails(details)
        }
    },
    watch: {
        'selectorModel.filters': {
            handler: function () {
                this.validateSelector()
            },
            deep: true
        },
        'selectorModel.selectedDateAndTimeRange': {
            handler: function () {
                this.validateSelector()
            },
            deep: true
        },
        'selectorModel.selectedDateAndTimeRangeCustomType': {
            handler: function () {
                this.validateSelector()
            },
            deep: true
        },
        'selectorModel.sinceDate': {
            handler: function () {
                this.validateSelector()
            },
            deep: true
        },
        'selectorModel.sinceTime': {
            handler: function () {
                this.validateSelector()
            },
            deep: true
        },
        'selectorModel.toDate': {
            handler: function () {
                this.validateSelector()
            },
            deep: true
        },
        'selectorModel.toTime': {
            handler: function () {
                this.validateSelector()
            },
            deep: true
        }
    }
}
